import React, { useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import ReactPlayer from 'react-player';
interface QuickStartFlowProps {
  navigateTo: (screen: string) => void;
  userData: any;
}
export const QuickStartFlow = ({
  navigateTo,
  userData
}: QuickStartFlowProps) => {
  const [step, setStep] = useState(0);
  const [currentPrefs, setCurrentPrefs] = useState({
    time: userData.timePreference || '10',
    energy: userData.energyLevel || 'medium',
    focus: userData.focusArea || 'mix',
    environment: userData.environment || 'none'
  });
  const [customizing, setCustomizing] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [previousMainVideo, setPreviousMainVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Sample video data based on preferences
  const getVideoRecommendation = prefs => {
    const videos = {
      stress: {
        title: 'Stress Relief Quick Workout',
        description: 'Calm your mind and relieve tension with gentle movements',
        duration: prefs.time,
        thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
        videoUrl: 'https://www.youtube.com/watch?v=inpok4MKVLM',
        intensity: prefs.energy,
        equipment: prefs.environment === 'none' ? 'No equipment' : 'Basic equipment',
        rating: 4.8,
        reviews: 240,
        id: 'stress-relief'
      },
      strength: {
        title: 'Strength Building Workout',
        description: 'Build muscle and increase power with targeted exercises',
        duration: prefs.time,
        thumbnail: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
        videoUrl: 'https://www.youtube.com/watch?v=DHOPWvO3ZcI',
        intensity: prefs.energy,
        equipment: prefs.environment === 'none' ? 'No equipment' : 'Basic equipment',
        rating: 4.9,
        reviews: 312,
        id: 'strength-building'
      },
      cardio: {
        title: 'Cardio Blast Session',
        description: 'Elevate your heart rate with high-energy movements',
        duration: prefs.time,
        thumbnail: 'https://images.unsplash.com/photo-1599058917765-a780eda07a3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1469&q=80',
        videoUrl: 'https://www.youtube.com/watch?v=ml6cT4AZdqI',
        intensity: prefs.energy,
        equipment: prefs.environment === 'none' ? 'No equipment' : 'Basic equipment',
        rating: 4.7,
        reviews: 189,
        id: 'cardio-blast'
      },
      mix: {
        title: 'Full Body Balanced Workout',
        description: 'A well-rounded session with variety for total body fitness',
        duration: prefs.time,
        thumbnail: 'https://images.unsplash.com/photo-1549576490-b0b4831ef60a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
        videoUrl: 'https://www.youtube.com/watch?v=UBMk30rjy0o',
        intensity: prefs.energy,
        equipment: prefs.environment === 'none' ? 'No equipment' : 'Basic equipment',
        rating: 4.9,
        reviews: 275,
        id: 'full-body'
      }
    };
    return videos[prefs.focus] || videos.mix;
  };
  // Get alternative videos that match the criteria
  const getAlternativeVideos = (prefs, mainVideo, prevMainVideo = null) => {
    // All possible alternative videos
    const allAlternatives = [{
      title: `${prefs.time}-min ${prefs.energy.charAt(0).toUpperCase() + prefs.energy.slice(1)} HIIT`,
      description: 'High-intensity interval training to maximize your time',
      duration: prefs.time,
      thumbnail: 'https://images.unsplash.com/photo-1434682881908-b43d0467b798?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1474&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=oAPCPjnU1wA',
      intensity: prefs.energy,
      equipment: prefs.environment === 'none' ? 'No equipment' : 'Basic equipment',
      rating: 4.7,
      reviews: 198,
      id: 'hiit'
    }, {
      title: `${prefs.time}-min Yoga Flow`,
      description: 'Fluid movements to improve flexibility and mindfulness',
      duration: prefs.time,
      thumbnail: 'https://images.unsplash.com/photo-1575052814086-f385e2e2ad1b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=j7rKKpwdXNE',
      intensity: prefs.energy,
      equipment: 'Yoga mat recommended',
      rating: 4.8,
      reviews: 215,
      id: 'yoga'
    }, {
      title: `${prefs.time}-min Core Workout`,
      description: 'Target your abs and build a strong core',
      duration: prefs.time,
      thumbnail: 'https://images.unsplash.com/photo-1616803689943-5601631c7fec?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=Xyd_fa5zoEU',
      intensity: prefs.energy,
      equipment: 'No equipment',
      rating: 4.6,
      reviews: 178,
      id: 'core'
    }, {
      title: `${prefs.time}-min Mobility Session`,
      description: 'Improve your range of motion and prevent injuries',
      duration: prefs.time,
      thumbnail: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
      videoUrl: 'https://www.youtube.com/watch?v=g_tea8ZNk5A',
      intensity: prefs.energy,
      equipment: 'No equipment',
      rating: 4.9,
      reviews: 203,
      id: 'mobility'
    }];
    // Filter out the main video and previous main video (if any)
    let filteredAlternatives = allAlternatives.filter(video => video.id !== mainVideo.id && (!prevMainVideo || video.id !== prevMainVideo.id));
    // If the previous main video exists and is not the same as the current main video,
    // add it to the alternatives
    if (prevMainVideo && prevMainVideo.id !== mainVideo.id) {
      filteredAlternatives = [prevMainVideo, ...filteredAlternatives];
    }
    // Return only the first 2 alternatives to match the UI
    return filteredAlternatives.slice(0, 2);
  };
  const updatePref = (field: string, value: string) => {
    setCurrentPrefs({
      ...currentPrefs,
      [field]: value
    });
  };
  const nextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Start workout
      navigateTo('home'); // For now, go back to home
    }
  };
  const startCustomizing = () => {
    setCustomizing(true);
    setStep(1); // Start from time selection
  };
  const browseAllWorkouts = () => {
    navigateTo('workouts');
  };
  // Handle when a user selects an alternative video
  const handleSelectAlternative = video => {
    // Store the current main video before replacing it
    const mainVideo = selectedVideo || getVideoRecommendation(currentPrefs);
    setPreviousMainVideo(mainVideo);
    // Set the selected alternative as the new main video
    setSelectedVideo(video);
  };
  const VideoPreview = ({
    video
  }) => {
    const [videoPlaying, setVideoPlaying] = useState(false);
    const handlePlayClick = () => {
      setVideoPlaying(true);
    };
    return <div className="mb-6" data-id="element-1415">
        <div className="relative rounded-xl overflow-hidden mb-3" style={{
        height: videoPlaying ? 'auto' : '200px'
      }} data-id="element-1416">
          {videoPlaying ? <div className="relative pt-[56.25%]" data-id="element-1417">
              <ReactPlayer url={video.videoUrl} playing={true} controls={true} width="100%" height="100%" style={{
            position: 'absolute',
            top: 0,
            left: 0
          }} onEnded={() => setVideoPlaying(false)} data-id="element-1418" />
            </div> : <>
              <img src={video.thumbnail} alt={video.title} className="w-full h-48 object-cover" data-id="element-1419" />
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center cursor-pointer" onClick={handlePlayClick} data-id="element-1420">
                <div className="w-16 h-16 bg-white bg-opacity-80 rounded-full flex items-center justify-center transform transition-transform hover:scale-110" data-id="element-1421">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500" data-id="element-1422">
                    <polygon points="5 3 19 12 5 21 5 3" data-id="element-1423"></polygon>
                  </svg>
                </div>
              </div>
              <div className="absolute top-3 right-3 bg-blue-500 text-white text-sm font-medium px-2 py-1 rounded-full" data-id="element-1424">
                {video.duration} min
              </div>
            </>}
        </div>
        <h3 className="text-lg font-bold mb-1" data-id="element-1425">{video.title}</h3>
        <p className="text-gray-600 text-sm mb-3" data-id="element-1426">{video.description}</p>
        <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-1427">
          <div className="flex items-center mr-4" data-id="element-1428">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-1429">
              <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" data-id="element-1430"></path>
              <path d="M12 8v4l2 2" data-id="element-1431"></path>
            </svg>
            {video.duration} min
          </div>
          <div className="flex items-center mr-4" data-id="element-1432">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-1433">
              <path d="M20 6 9 17l-5-5" data-id="element-1434"></path>
            </svg>
            {video.equipment}
          </div>
          <div className="flex items-center" data-id="element-1435">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 text-yellow-500" data-id="element-1436">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-1437"></polygon>
            </svg>
            {video.rating} ({video.reviews})
          </div>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700" data-id="element-1438">
          <p data-id="element-1439">
            Intensity:{' '}
            {video.intensity.charAt(0).toUpperCase() + video.intensity.slice(1)}
          </p>
        </div>
      </div>;
  };
  const AlternativeVideoCard = ({
    video,
    onSelect
  }) => {
    return <div className="flex-1 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-2" onClick={() => onSelect(video)} data-id="element-1440">
        <div className="relative rounded-lg overflow-hidden mb-2" data-id="element-1441">
          <img src={video.thumbnail} alt={video.title} className="w-full h-24 object-cover" data-id="element-1442" />
          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center" data-id="element-1443">
            <div className="w-10 h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center" data-id="element-1444">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500" data-id="element-1445">
                <polygon points="5 3 19 12 5 21 5 3" data-id="element-1446"></polygon>
              </svg>
            </div>
          </div>
          <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full" data-id="element-1447">
            {video.duration} min
          </div>
        </div>
        <h4 className="text-sm font-medium line-clamp-1" data-id="element-1448">{video.title}</h4>
        <p className="text-xs text-gray-500 line-clamp-1" data-id="element-1449">
          {video.description}
        </p>
        <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-1450">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 text-yellow-500" data-id="element-1451">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-1452"></polygon>
          </svg>
          {video.rating}
        </div>
      </div>;
  };
  const renderStep = () => {
    switch (step) {
      case 0:
        {
          const recommendedVideo = selectedVideo || getVideoRecommendation(currentPrefs);
          const alternativeVideos = getAlternativeVideos(currentPrefs, recommendedVideo, previousMainVideo);
          return <div className="p-6 flex flex-col h-full" data-id="element-1453">
            <div className="mb-6" data-id="element-1454">
              <button onClick={() => navigateTo('home')} className="text-gray-500 mb-4" data-id="element-1455">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1456">
                Ready for your workout?
              </h1>
              <p className="text-gray-600 mb-4" data-id="element-1457">
                We've picked a great workout for you based on your preferences.
              </p>
              {/* Video recommendation preview */}
              <VideoPreview video={recommendedVideo} data-id="element-1458" />
              {/* Alternative videos section */}
              <div className="mb-4" data-id="element-1459">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1460">
                  Other options you might like:
                </h3>
                <div className="flex gap-3 mb-3" data-id="element-1461">
                  {alternativeVideos.map((video, index) => <AlternativeVideoCard key={index} video={video} onSelect={handleSelectAlternative} data-id="element-1462" />)}
                </div>
                <button className="text-blue-500 text-sm font-medium flex items-center" onClick={() => browseAllWorkouts()} data-id="element-1463">
                  Browse all
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1" data-id="element-1464">
                    <path d="m9 18 6-6-6-6" data-id="element-1465"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="mt-auto space-y-4" data-id="element-1466">
              <Button onClick={nextStep} variant="primary" size="lg" className="w-full" data-id="element-1467">
                Start This Workout
              </Button>
              <Button onClick={startCustomizing} variant="outline" size="lg" className="w-full" data-id="element-1468">
                Customize Workout
              </Button>
            </div>
          </div>;
        }
      case 1:
        return <div className="p-6 flex flex-col h-full" data-id="element-1469">
            <div className="mb-6" data-id="element-1470">
              <button onClick={() => customizing ? setStep(0) : navigateTo('home')} className="text-gray-500 mb-4" data-id="element-1471">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1472">
                How much time do you have today?
              </h1>
            </div>
            <div className="space-y-4 mb-8" data-id="element-1473">
              {['5', '10', '15'].map(time => <Card key={time} onClick={() => updatePref('time', time)} active={currentPrefs.time === time} className="flex items-center p-5" data-id="element-1474">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-1475">
                    <span className="text-blue-500 font-medium" data-id="element-1476">{time}</span>
                  </div>
                  <span className="text-lg" data-id="element-1477">{time} minutes</span>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-1478">
              <Button onClick={nextStep} variant="primary" fullWidth data-id="element-1479">
                Continue
              </Button>
            </div>
          </div>;
      case 2:
        return <div className="p-6 flex flex-col h-full" data-id="element-1480">
            <div className="mb-6" data-id="element-1481">
              <button onClick={() => setStep(1)} className="text-gray-500 mb-4" data-id="element-1482">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1483">
                How's your energy level right now?
              </h1>
            </div>
            <div className="space-y-4 mb-8" data-id="element-1484">
              {[{
              value: 'low',
              label: 'Low',
              emoji: '🌙'
            }, {
              value: 'medium',
              label: 'Medium',
              emoji: '🔆'
            }, {
              value: 'high',
              label: 'High',
              emoji: '⚡'
            }].map(energy => <Card key={energy.value} onClick={() => updatePref('energy', energy.value)} active={currentPrefs.energy === energy.value} className="flex items-center p-5" data-id="element-1485">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-xl" data-id="element-1486">
                    {energy.emoji}
                  </div>
                  <span className="text-lg" data-id="element-1487">{energy.label}</span>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-1488">
              <Button onClick={nextStep} variant="primary" fullWidth data-id="element-1489">
                Continue
              </Button>
            </div>
          </div>;
      case 3:
        return <div className="p-6 flex flex-col h-full" data-id="element-1490">
            <div className="mb-6" data-id="element-1491">
              <button onClick={() => setStep(2)} className="text-gray-500 mb-4" data-id="element-1492">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1493">
                What would you like to focus on?
              </h1>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1494">
              {[{
              value: 'stress',
              label: 'Stress relief',
              emoji: '🌿'
            }, {
              value: 'strength',
              label: 'Strength',
              emoji: '💪'
            }, {
              value: 'cardio',
              label: 'Cardio',
              emoji: '❤️'
            }, {
              value: 'mix',
              label: 'Mix',
              emoji: '🎲'
            }].map(focus => <Card key={focus.value} onClick={() => updatePref('focus', focus.value)} active={currentPrefs.focus === focus.value} className="p-4 flex flex-col items-center text-center" data-id="element-1495">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2 text-2xl" data-id="element-1496">
                    {focus.emoji}
                  </div>
                  <h3 className="font-medium" data-id="element-1497">{focus.label}</h3>
                </Card>)}
            </div>
            <div className="mt-auto" data-id="element-1498">
              <Button onClick={nextStep} variant="primary" fullWidth data-id="element-1499">
                Continue
              </Button>
            </div>
          </div>;
      case 4:
        {
          // Reset selections for final screen to ensure fresh recommendations
          setSelectedVideo(null);
          setPreviousMainVideo(null);
          // Get video based on user's selected preferences
          const finalVideo = getVideoRecommendation(currentPrefs);
          const altVideos = getAlternativeVideos(currentPrefs, finalVideo);
          return <div className="p-6 flex flex-col h-full" data-id="element-1500">
            <div className="mb-4" data-id="element-1501">
              <button onClick={() => setStep(3)} className="text-gray-500 mb-4" data-id="element-1502">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1503">
                Your Workout is Ready!
              </h1>
              <p className="text-gray-600 mb-4" data-id="element-1504">
                Based on your preferences, we recommend:
              </p>
            </div>
            {/* Video preview component */}
            <VideoPreview video={finalVideo} data-id="element-1505" />
            {/* Alternative videos section */}
            <div className="mb-4" data-id="element-1506">
              <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1507">
                Other options you might like:
              </h3>
              <div className="flex gap-3 mb-3" data-id="element-1508">
                {altVideos.map((video, index) => <AlternativeVideoCard key={index} video={video} onSelect={handleSelectAlternative} data-id="element-1509" />)}
              </div>
              <button className="text-blue-500 text-sm font-medium flex items-center" onClick={() => browseAllWorkouts()} data-id="element-1510">
                Browse all
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1" data-id="element-1511">
                  <path d="m9 18 6-6-6-6" data-id="element-1512"></path>
                </svg>
              </button>
            </div>
            <div className="mt-auto space-y-4" data-id="element-1513">
              <Button onClick={nextStep} variant="primary" fullWidth size="lg" data-id="element-1514">
                Start Workout
              </Button>
            </div>
          </div>;
        }
      default:
        return null;
    }
  };
  return <div className="flex flex-col h-full min-h-screen bg-white" data-id="element-1515">
      {renderStep()}
    </div>;
};